import MultiDropDown from '@components/common/dropDown/multiDropDown';
import Dimm from '@components/pass/interview/common/dimm';
import { PooledAccQuestionDto } from '@domain/rs/pass/interview/question/PooledAccQuestionListRs';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useAccPracticeQuestions } from '@repository/interviewPractices/pooledQuestions/usePooledQuestions';
import useCheckAuthority from '@utils/hooks/useCheckAuthority';
import classnames from 'classnames/bind';
import { FC, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useInterview } from '../../../context/interviewContext';
import styles from './index.module.scss';

import Text from '@components/common/text';
import { FontStyle } from '@domain/constant/fontStyle';

const cx = classnames.bind(styles);

interface QuestionProps extends PooledAccQuestionDto {
  type: 'WEAKNESS' | 'STRENGTH';
}

interface QuestionItemProps {
  data: QuestionProps;
  checked: boolean;
  onClick: () => void;
}

const QuestionItem: FC<QuestionItemProps> = ({ data, checked, onClick }) => {
  const isStrength = data.type === 'STRENGTH';
  const text = isStrength ? '강점' : '약점';

  return (
    <button className={cx('questionItem', { checked })} onClick={onClick}>
      <div className={cx('sectionTag')}>
        <div className={cx('tag', 'isStrength')}>역량검사 결과 기반 질문</div>
        <div className={cx('tag')}>{text}</div>
        <div className={cx('tag', 'sectionName')}>{data.description}</div>
      </div>
      <div className={cx('description')}>
        <span className={cx('intro')}>Q.</span>
        <span className={cx('question')}>{data.questionText}</span>
      </div>
    </button>
  );
};

interface Props {
  selectedCode: string | null;
}

const QuestionList: FC<Props> = ({ selectedCode }) => {
  const { selectedQuestionList, updateSelectedQuestionList, selectedAccQuestion, policies, selectAccQuestion } =
    useInterview();
  const { data: userInfo } = useUserInfo();
  const { isAuthorityUser } = useCheckAuthority(userInfo);
  const checked = (sn: number) =>
    !!selectedAccQuestion?.find((v) => v.accCode === selectedCode)?.questionSn.includes(sn);
  const methods = useForm();
  const { watch } = methods;
  const filterValue = watch('filter')?.length ? watch('filter').map((v: any) => v.value) : ['WEAKNESS', 'STRENGTH'];
  const isClickable = selectedQuestionList.length < policies.interviewPracticeMaxQuestionSize;
  const { data } = useAccPracticeQuestions(selectedCode ?? '', {
    enabled: selectedCode !== null && isAuthorityUser,
  });

  const shuffledQuestions = () => {
    const weaknessQuestions =
      data?.weaknessQuestions.map((q) => {
        return { ...q, type: 'WEAKNESS' as const };
      }) ?? [];
    const strengthQuestions =
      data?.strengthQuestions.map((q) => {
        return { ...q, type: 'STRENGTH' as const };
      }) ?? [];
    const questions = [...strengthQuestions, ...weaknessQuestions];

    return questions;
  };

  const filterQuestions = shuffledQuestions().filter((q) => filterValue.includes(q.type));

  const questionEls = filterQuestions.map((q) => (
    <QuestionItem
      key={q.sn}
      data={q}
      checked={checked(q.sn)}
      onClick={() => {
        if (!checked(q.sn) && !isClickable) return;
        updateSelectedQuestionList(q, checked(q.sn) ? false : true);

        if (selectedCode) selectAccQuestion(selectedCode, q.sn);
      }}
    />
  ));

  const isEmpty = userInfo && data && filterQuestions.length === 0;

  return (
    <div className={cx('questionListWrap')}>
      <div className={cx('titleWrap')}>
        <div className={cx('title')}>
          역량검사 결과 기반 질문 <span className={cx('highlightText')}>{filterQuestions.length}</span>개
        </div>
        <div className={cx('btnWrap')}>
          <MultiDropDown
            className={cx('filterJobGroup')}
            control={methods.control}
            name={'filter'}
            options={[
              { label: '강점', value: 'STRENGTH' },
              { label: '약점', value: 'WEAKNESS' },
            ]}
            placeholder={'강점・약점'}
          />
        </div>
      </div>
      {!isAuthorityUser && <Dimm style={{ width: 1200, height: 690, marginTop: 16 }} />}
      <div className={cx('questionList')}>
        {isEmpty && (
          <div className={cx('emptyArea')}>
            <div className={cx('emptyIcon')} />
            <Text fontStyle={FontStyle.B1_B}>면접 질문이 없어요</Text>
          </div>
        )}
        {questionEls}
      </div>
    </div>
  );
};

export default QuestionList;
